import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ProfileOverviewService} from '@gatewee/services/profile-overview.service';
import {ProfilePhotoService} from '@gatewee/services/profile-photo.service';
import {ProfileSyncService} from '@gatewee/services/profile-sync.service';
import {ProfileOverviewUser} from '@gatewee/types/profile-overview';
import {UtilsModule} from '@gatewee/utils';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {TuiAutoColorPipe} from '@taiga-ui/core/pipes/auto-color';
import {TuiInitialsPipe} from '@taiga-ui/core/pipes/initials';
import {TuiAvatar} from '@taiga-ui/kit/components/avatar';
import {catchError, finalize, of, Subject, takeUntil} from 'rxjs';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';

const acceptedImageTypes = ['image/jpeg', 'image/png'] as const;

@Component({
  selector: 'gatewee-profile-overview',
  templateUrl: './gatewee-profile-overview.component.html',
  styleUrls: ['./gatewee-profile-overview.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    UtilsModule,
    GateweeButtonComponent,
    GateweeSkeletonComponent,
    TuiAvatar,
    TuiInitialsPipe,
    TuiAutoColorPipe,
  ],
})
export class GateweeProfileOverviewComponent implements OnInit, OnDestroy {
  profile: ProfileOverviewUser | null = null;
  isLoading = true;
  hasError = false;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  uploadError: string | null = null;
  uploading = false;

  readonly acceptedTypes = acceptedImageTypes;
  readonly maxSizeMb = 2;

  private readonly destroy$ = new Subject<void>();
  private readonly profileOverview = inject(ProfileOverviewService);
  private readonly profilePhoto = inject(ProfilePhotoService);
  private readonly profileSync = inject(ProfileSyncService);
  private readonly alerts = inject(TuiAlertService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.loadProfile();

    this.profileSync.profileUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.profile) {
          this.profile = {
            ...this.profile,
            profilePhoto: this.profilePhoto.getCurrentPhotoUrl(),
          };
        }
      });
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasPreview(): boolean {
    return !!this.previewUrl;
  }

  get displayPhotoUrl(): string {
    return this.profile?.profilePhoto ?? '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.validateAndSetFile(input.files?.[0]);
    input.value = '';
  }

  cancelPreview(): void {
    this.revokePreview();
    this.selectedFile = null;
    this.uploadError = null;
  }

  confirmUpload(): void {
    if (!this.selectedFile || this.uploading || !this.profile) {
      return;
    }

    this.uploading = true;
    this.uploadError = null;
    this.profilePhoto.upload(this.selectedFile).subscribe({
      next: response => {
        this.profile = {
          ...this.profile!,
          profilePhoto: response.profilePhoto,
        };
        this.cancelPreview();
        this.uploading = false;
        this.alerts
          .open(
            this.translate.instant('features.profile.photo.uploadSuccess'),
            {
              label: this.translate.instant('common.success'),
              appearance: 'success',
            },
          )
          .subscribe();
      },
      error: err => {
        this.uploading = false;
        this.alerts
          .open(this.resolveErrorMessage(err), {
            label: this.translate.instant('common.error'),
            appearance: 'error',
          })
          .subscribe();
      },
    });
  }

  private loadProfile(): void {
    this.isLoading = true;
    this.hasError = false;
    this.profileOverview
      .getProfile()
      .pipe(
        catchError(() => {
          this.hasError = true;
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe(profile => {
        this.profile = profile;
      });
  }

  private validateAndSetFile(file: File | undefined): void {
    this.uploadError = null;

    if (!file) {
      return;
    }

    if (
      !this.acceptedTypes.includes(
        file.type as (typeof acceptedImageTypes)[number],
      )
    ) {
      this.uploadError = this.translate.instant(
        'features.profile.photo.errors.invalidFormat',
      );
      return;
    }

    if (file.size > this.maxSizeMb * 1024 * 1024) {
      this.uploadError = this.translate.instant(
        'features.profile.photo.errors.maxSizeExceeded',
        {size: this.maxSizeMb},
      );
      return;
    }

    this.revokePreview();
    this.selectedFile = file;
    this.previewUrl = URL.createObjectURL(file);
  }

  private revokePreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  private resolveErrorMessage(error: unknown): string {
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      (error as {error?: {message?: string}}).error?.message
    ) {
      return String((error as {error: {message: string}}).error.message);
    }
    return this.translate.instant('common.errors.unknown');
  }
}
